<script setup lang="ts">
import { computed, inject, onBeforeUnmount, ref, unref } from 'vue'
import FieldError from './FieldError.vue'
import FieldWarning from './FieldWarning.vue'
import { maxLength } from '@/lib/inputFormat'
import { usStates } from './usStates'
import { addressLookupKey, newLookupSession, type AddressLookup } from './addressLookup'
import { ApiRequestError } from '@/api/client'
import type { IntakeAddressSuggestion } from '@/api/schemas'
import {
  addressOf,
  addressParts,
  type AddressFieldKey,
  type AddressPrefix,
  type FieldValidation,
  type IntakeFormData,
} from './formDefinitions'

// One US address as street / unit / city / state / ZIP. When the page provides
// an address lookup, the street field is also a combobox: typing suggests
// addresses, and picking one fills every part. Without a lookup, or once it
// fails, the fields work as plain inputs.
const props = defineProps<{
  prefix: AddressPrefix
  legend: string
  fieldValidation: (key: string) => FieldValidation | undefined
  /** Non-blocking warnings for the whole address, from addressFieldWarnings(). */
  warnings?: string[] | undefined
}>()
const form = defineModel<IntakeFormData>('form', { required: true })

const lookupRef = inject(addressLookupKey, ref<AddressLookup>())

type Part = (typeof addressParts)[number]
function key(part: Part): AddressFieldKey {
  return `${props.prefix}_${part}`
}
function fieldError(part: Part): string | undefined {
  const field = props.fieldValidation(key(part))
  if (field?.$error !== true) return undefined
  const first = field.$errors[0]
  return first === undefined ? undefined : unref(first.$message)
}
function ariaInvalid(part: Part) {
  return fieldError(part) === undefined ? undefined : 'true'
}
function describedBy(part: Part) {
  return fieldError(part) === undefined ? undefined : `err-${key(part)}`
}
// Autofill keeps each address to its own section, so a mailing address never fills the physical one.
function autocomplete(token: string) {
  return `section-${props.prefix} ${token}`
}

// Warnings never block. They follow the current value, so a restored address
// shows its warning without being edited, but they wait while the visitor is
// changing this address, and give way to an error on any of its parts.
const snapshotAtFocus = ref<string>()
const snapshot = () => JSON.stringify(addressOf(form.value, props.prefix))
const shownWarnings = computed(() => {
  const warnings = props.warnings ?? []
  if (addressParts.some((part) => fieldError(part) !== undefined)) return []
  if (snapshotAtFocus.value !== undefined && snapshot() !== snapshotAtFocus.value) return []
  return warnings
})
function onFocusIn() {
  snapshotAtFocus.value ??= snapshot()
}
function onFocusOut(event: FocusEvent) {
  const next = event.relatedTarget
  if (next instanceof Node && (event.currentTarget as HTMLElement).contains(next)) return
  snapshotAtFocus.value = undefined
}

const listId = computed(() => `${props.prefix}-suggestions`)
const suggestions = ref<IntakeAddressSuggestion[]>([])
const active = ref(-1)
const open = computed(() => suggestions.value.length > 0)
const lookupFailed = ref(false)
const enabled = computed(() => lookupRef.value !== undefined && !lookupFailed.value)
const announcement = ref('')

let session = newLookupSession()
let timer: ReturnType<typeof setTimeout> | undefined
let inflight: AbortController | undefined

function close() {
  suggestions.value = []
  active.value = -1
}
function stop() {
  clearTimeout(timer)
  inflight?.abort()
  inflight = undefined
}
function disable() {
  stop()
  close()
  lookupFailed.value = true
}

async function loadSuggestions(lookup: AddressLookup, query: string) {
  const controller = new AbortController()
  inflight = controller
  try {
    const rows = await lookup.suggest(query, session, controller.signal)
    if (controller.signal.aborted) return
    suggestions.value = rows
    active.value = -1
    announcement.value = rows.length
      ? `${rows.length} address suggestion${rows.length === 1 ? '' : 's'}. Use the arrow keys to choose.`
      : ''
  } catch (cause) {
    if (controller.signal.aborted) return
    // A rejected query is just a bad keystroke; anything else means the lookup is down.
    if (!(cause instanceof ApiRequestError && cause.status === 400)) disable()
  }
}

function onStreetInput(event: Event) {
  const lookup = lookupRef.value
  if (!enabled.value || lookup === undefined) return
  stop()
  // Browser autofill fills every part itself and needs no suggestions; it fires a plain Event.
  const inputType = event instanceof InputEvent ? event.inputType : undefined
  if (inputType === undefined || inputType === 'insertReplacementText') {
    close()
    return
  }
  const query = (event.target as HTMLInputElement).value.trim()
  if (query.length < 3) {
    close()
    return
  }
  timer = setTimeout(() => void loadSuggestions(lookup, query), 250)
}

function onStreetKeydown(event: KeyboardEvent) {
  if (!open.value) return
  const count = suggestions.value.length
  if (event.key === 'ArrowDown') {
    event.preventDefault()
    active.value = (active.value + 1) % count
  } else if (event.key === 'ArrowUp') {
    event.preventDefault()
    active.value = active.value <= 0 ? count - 1 : active.value - 1
  } else if (event.key === 'Enter' && active.value >= 0) {
    event.preventDefault()
    const chosen = suggestions.value[active.value]
    if (chosen) void choose(chosen)
  } else if (event.key === 'Escape') {
    event.preventDefault()
    close()
  }
}

const street = ref<HTMLInputElement>()
async function choose(suggestion: IntakeAddressSuggestion) {
  const lookup = lookupRef.value
  if (lookup === undefined) return
  stop()
  close()
  const chosenSession = session
  session = newLookupSession()
  try {
    const address = await lookup.resolve(suggestion.place_id, chosenSession)
    const state = usStates.find((option) => option.code === address.state)?.code ?? ''
    form.value[key('street')] = address.street ?? suggestion.main
    if (address.unit !== undefined && address.unit !== '') form.value[key('unit')] = address.unit
    form.value[key('city')] = address.city ?? ''
    form.value[key('state')] = state
    form.value[key('zip')] = address.zip ?? ''
    announcement.value = 'Address filled in. Check each part, and add a unit if there is one.'
  } catch {
    form.value[key('street')] = suggestion.main
    disable()
  }
  // A change event lets the page validate and autosave the step, as if the visitor typed it.
  street.value?.dispatchEvent(new Event('change', { bubbles: true }))
}

onBeforeUnmount(stop)
</script>

<template>
  <fieldset
    class="address"
    :aria-describedby="shownWarnings.length ? `warn-${prefix}` : undefined"
    @focusin="onFocusIn"
    @focusout="onFocusOut"
  >
    <legend>{{ legend }}</legend>
    <label>
      Street address
      <span class="combo">
        <input
          :id="'field-' + key('street')"
          ref="street"
          v-model="form[key('street')]"
          :autocomplete="autocomplete('address-line1')"
          autocapitalize="words"
          :maxlength="maxLength.text"
          :role="enabled ? 'combobox' : undefined"
          :aria-autocomplete="enabled ? 'list' : undefined"
          :aria-expanded="enabled ? open : undefined"
          :aria-controls="enabled ? listId : undefined"
          :aria-activedescendant="active >= 0 ? `${listId}-${active}` : undefined"
          :aria-invalid="ariaInvalid('street')"
          :aria-describedby="describedBy('street')"
          @input="onStreetInput"
          @keydown="onStreetKeydown"
          @blur="close"
        />
        <ul
          v-if="enabled"
          v-show="open"
          :id="listId"
          role="listbox"
          class="suggestions"
          :aria-label="`${legend} suggestions`"
        >
          <li
            v-for="(suggestion, index) in suggestions"
            :id="`${listId}-${index}`"
            :key="suggestion.place_id"
            role="option"
            tabindex="-1"
            :aria-selected="index === active"
            :class="{ active: index === active }"
            @mousedown.prevent="choose(suggestion)"
          >
            <strong>{{ suggestion.main }}</strong> <span>{{ suggestion.secondary }}</span>
          </li>
          <li class="attribution" aria-hidden="true">Suggestions by Google</li>
        </ul>
      </span>
      <FieldError :id="'err-' + key('street')" :message="fieldError('street')" />
    </label>
    <label>
      <span>Apt, suite, or unit <span class="optional">(optional)</span></span>
      <input
        :id="'field-' + key('unit')"
        v-model="form[key('unit')]"
        :autocomplete="autocomplete('address-line2')"
        :maxlength="maxLength.text"
      />
    </label>
    <div class="city-state-zip">
      <label>
        City
        <input
          :id="'field-' + key('city')"
          v-model="form[key('city')]"
          :autocomplete="autocomplete('address-level2')"
          autocapitalize="words"
          :maxlength="maxLength.text"
          :aria-invalid="ariaInvalid('city')"
          :aria-describedby="describedBy('city')"
        />
        <FieldError :id="'err-' + key('city')" :message="fieldError('city')" />
      </label>
      <label>
        State
        <select
          :id="'field-' + key('state')"
          v-model="form[key('state')]"
          :autocomplete="autocomplete('address-level1')"
          :aria-invalid="ariaInvalid('state')"
          :aria-describedby="describedBy('state')"
        >
          <option value="">Choose one</option>
          <option v-for="state in usStates" :key="state.code" :value="state.code">
            {{ state.name }}
          </option>
        </select>
        <FieldError :id="'err-' + key('state')" :message="fieldError('state')" />
      </label>
      <label>
        ZIP code
        <input
          :id="'field-' + key('zip')"
          v-model="form[key('zip')]"
          :autocomplete="autocomplete('postal-code')"
          inputmode="numeric"
          spellcheck="false"
          maxlength="10"
          :aria-invalid="ariaInvalid('zip')"
          :aria-describedby="describedBy('zip')"
        />
        <FieldError :id="'err-' + key('zip')" :message="fieldError('zip')" />
      </label>
    </div>
    <FieldWarning :id="`warn-${prefix}`" :messages="shownWarnings" />
    <p class="sr-only" aria-live="polite">{{ announcement }}</p>
  </fieldset>
</template>

<style scoped>
/* Mirrors IntakeStepRenderer's controls; scoped styles do not cross the component boundary. */
.address {
  border: 0;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 1rem;
  min-width: 0;
}
legend {
  font-weight: 700;
  padding: 0;
  margin-bottom: 1rem;
}
label {
  display: grid;
  gap: 0.35rem;
  font-size: 0.93rem;
  font-weight: 650;
}
.optional {
  font-weight: 400;
  color: #526057;
}
input,
select {
  box-sizing: border-box;
  width: 100%;
  padding: 0.62rem 0.7rem;
  border: 1px solid #aeb9ac;
  border-radius: 3px;
  background: #fff;
  color: var(--ink);
  font: inherit;
  font-weight: 400;
}
input:focus-visible,
select:focus-visible {
  outline: 3px solid #b9c967;
  outline-offset: 2px;
  border-color: var(--pine);
}
input[aria-invalid='true'],
select[aria-invalid='true'] {
  border-color: #a53c28;
  box-shadow: 0 0 0 1px #a53c28;
}
.city-state-zip {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1.5fr) minmax(0, 1.2fr);
  gap: 1rem;
  align-items: start;
}
.combo {
  position: relative;
  display: block;
}
.suggestions {
  position: absolute;
  z-index: 10;
  left: 0;
  right: 0;
  top: calc(100% + 2px);
  margin: 0;
  padding: 0.25rem 0;
  list-style: none;
  background: #fff;
  border: 1px solid #aeb9ac;
  border-radius: 3px;
  box-shadow: 0 8px 22px #25352a24;
}
.suggestions li {
  padding: 0.55rem 0.7rem;
  font-weight: 400;
  line-height: 1.35;
  cursor: pointer;
}
.suggestions li span {
  color: #526057;
  font-size: 0.88rem;
}
.suggestions li.active,
.suggestions li:not(.attribution):hover {
  background: #eef3dc;
}
.suggestions .attribution {
  padding: 0.3rem 0.7rem 0;
  font-size: 0.75rem;
  color: #68746c;
  cursor: default;
  text-align: right;
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
}
:deep(.field-error) {
  margin: -0.2rem 0 0;
  color: #a53c28;
  font-size: 0.85rem;
  font-weight: 650;
  line-height: 1.4;
}
:deep(.field-warning) {
  margin: -0.2rem 0 0;
  color: #6b5310;
  font-size: 0.85rem;
  font-weight: 400;
  line-height: 1.4;
}
@media (max-width: 600px) {
  .city-state-zip {
    grid-template-columns: 1fr;
  }
}
</style>
